import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useOrderForm } from 'services/repositories/orderForms/orderForms';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { initializeOrderFormCategories, setOrderFormData, useOrderFormStore } from 'stores/orderForm/orderForm';
import { RouteParams } from 'support/helpers/types/types';
import { OrderFormsRoutes } from 'support/types';

import { OrderFormPageLayout } from 'components/OrderFormPageLayout/OrderFormPageLayout';

import { useOrderFormCalculations } from './hooks/useOrderFormCalculations';
import { useOrderFormWithValidatedStorage } from './hooks/useOrderFormWithValidatedStorage';

import { EmptySearchResultsList } from './components/EmptySearchResultsList';
import { OrderFormContent } from './components/OrderFormContent/OrderFormContent';
import { OrderFormHeaderContent } from './components/OrderFormHeaderContent/OrderFormHeaderContent';
import { OrderFormSidebar } from './components/OrderFormSidebar/OrderFormSidebar';

export const OrderForm = () => {
  const { supplierSlug } = useParams<RouteParams<OrderFormsRoutes.root>>();
  const { data: orderFormData, isLoading: isLoadingOrderForm } = useOrderForm({
    variables: { supplierSlugName: supplierSlug },
  });
  const { categories: savedCategories, filteredCategories } = useOrderFormStore();

  const { data: currentPartner, isLoading: isLoadingCurrentPartner } = useCurrentPartner();
  const isLoading = isLoadingOrderForm || isLoadingCurrentPartner;

  const formMethods = useOrderFormWithValidatedStorage(orderFormData?.data?.categories);
  useOrderFormCalculations(orderFormData?.data, formMethods.watch);

  useEffect(() => {
    if (!orderFormData?.data) {
      return;
    }

    setOrderFormData(orderFormData.data.minimum_order_value, orderFormData.data.currency);

    initializeOrderFormCategories(orderFormData.data.categories);
  }, [orderFormData?.data]);

  const emptySearchResults = filteredCategories?.length === 0 && savedCategories && savedCategories.length > 0;

  const sidebarContent = filteredCategories ? (
    <OrderFormSidebar withoutMatches={!!emptySearchResults} categories={filteredCategories} />
  ) : null;

  return (
    <OrderFormPageLayout headerContent={<OrderFormHeaderContent />} isLoading={isLoading} sidebar={sidebarContent}>
      {emptySearchResults ? (
        <EmptySearchResultsList />
      ) : filteredCategories ? (
        <OrderFormContent
          categories={filteredCategories}
          formMethods={formMethods}
          partnerName={currentPartner?.name}
        />
      ) : null}
    </OrderFormPageLayout>
  );
};
